import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Plus, Loader2, Edit, X, CheckCircle2, Clock, Target,
  CalendarDays, AlertTriangle, PlayCircle, Hourglass
} from "lucide-react";

const STATUSES = {
  in_progress: { label: "In Progress", icon: PlayCircle, color: "text-sky-600", bg: "bg-sky-100", border: "border-sky-200", ring: "ring-sky-400" },
  waiting_client: { label: "Waiting for Client", icon: Hourglass, color: "text-amber-600", bg: "bg-amber-100", border: "border-amber-200", ring: "ring-amber-400" },
  completed: { label: "Completed", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-100", border: "border-emerald-200", ring: "ring-emerald-400" }
};

function StatusButton({ status, currentStatus, onClick, compact = false }) {
  const cfg = STATUSES[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  const isActive = currentStatus === status;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all border ${
        isActive
          ? `${cfg.bg} ${cfg.color} ${cfg.border} ring-2 ${cfg.ring} ring-offset-1`
          : "bg-white border-neutral-200 text-neutral-500 hover:border-neutral-300 hover:text-neutral-700"
      }`}
      data-testid={`status-btn-${status}`}
      title={cfg.label}
    >
      <Icon className="w-3 h-3" />
      {!compact && cfg.label}
    </button>
  );
}

function StatusBadge({ status }) {
  const s = status || "in_progress";
  const cfg = STATUSES[s];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.color} ${cfg.border} border`}>
      <Icon className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
}

export function MilestonesPanel({ project, onUpdate }) {
  const [newTitle, setNewTitle] = useState("");
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [newDeps, setNewDeps] = useState([]);
  const [newCritical, setNewCritical] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");

  const milestones = project?.milestones || [];
  const total = milestones.length;
  const done = milestones.filter(m => m.done || m.status === "completed").length;
  const inProgress = milestones.filter(m => (m.status || "in_progress") === "in_progress" && !m.done).length;
  const waiting = milestones.filter(m => m.status === "waiting_client").length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const isOverdue = (ms) => {
    if (ms.done || ms.status === "completed") return false;
    const due = ms.end_date || ms.due_date;
    if (!due) return false;
    return new Date() > new Date(due);
  };

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const payload = { title: newTitle.trim(), status: "in_progress", dependencies: newDeps, is_critical: newCritical };
      if (newStartDate) payload.start_date = newStartDate;
      if (newEndDate) payload.end_date = newEndDate;
      const res = await authFetch(`${API}/consultant/projects/${project.project_id}/milestones`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const d = await res.json();
        onUpdate({ milestones: [...milestones, d.milestone], percentage_complete: pct });
        setNewTitle(""); setNewStartDate(""); setNewEndDate(""); setNewDeps([]); setNewCritical(false);
        toast.success("Milestone added");
      } else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed"); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
    setAdding(false);
  };

  const handleStatusChange = async (ms, newStatus) => {
    const msId = ms.id || ms.title;
    try {
      const res = await authFetch(`${API}/consultant/projects/${project.project_id}/milestones/${encodeURIComponent(msId)}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        const d = await res.json();
        onUpdate(d);
        toast.success(`Status: ${STATUSES[newStatus]?.label}`);
      }
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const handleSaveEdit = async (ms) => {
    if (!editTitle.trim()) { setEditingId(null); return; }
    const msId = ms.id || ms.title;
    const payload = { title: editTitle.trim() };
    if (editStartDate !== undefined) payload.start_date = editStartDate || null;
    if (editEndDate !== undefined) payload.end_date = editEndDate || null;
    try {
      const res = await authFetch(`${API}/consultant/projects/${project.project_id}/milestones/${encodeURIComponent(msId)}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (res.ok) { const d = await res.json(); onUpdate(d); setEditingId(null); toast.success("Updated"); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const handleDelete = async (ms) => {
    const msId = ms.id || ms.title;
    try {
      const res = await authFetch(`${API}/consultant/projects/${project.project_id}/milestones/${encodeURIComponent(msId)}`, { method: "DELETE" });
      if (res.ok) { const d = await res.json(); onUpdate(d); toast.success("Removed"); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

  const daysUntil = (d) => {
    if (!d) return null;
    return Math.ceil((new Date(d) - new Date()) / 86400000);
  };

  return (
    <div className="space-y-4" data-testid="milestones-panel">
      {/* Progress Summary */}
      <Card className="border-emerald-200 bg-emerald-50/50">
        <CardContent className="py-4 px-5">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-base font-semibold text-emerald-800">{done}/{total} Milestones Complete</h3>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-[10px] text-sky-600 flex items-center gap-1"><PlayCircle className="w-2.5 h-2.5" />{inProgress} In Progress</span>
                <span className="text-[10px] text-amber-600 flex items-center gap-1"><Hourglass className="w-2.5 h-2.5" />{waiting} Waiting</span>
                <span className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5" />{done} Done</span>
              </div>
            </div>
            <span className="text-3xl font-bold text-emerald-700 tabular-nums">{pct}%</span>
          </div>
          <Progress value={pct} className="h-3" />
        </CardContent>
      </Card>

      {/* Add Milestone */}
      <Card className="border-neutral-200">
        <CardContent className="py-3 px-4 space-y-2">
          <div className="flex gap-2">
            <Input
              value={newTitle} onChange={e => setNewTitle(e.target.value)}
              placeholder="Add a milestone..." className="flex-1"
              data-testid="add-milestone-input"
              onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
            />
            <Button onClick={handleAdd} disabled={adding || !newTitle.trim()} data-testid="add-milestone-btn">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <Label className="text-[10px] text-neutral-400 uppercase">Start Date</Label>
              <Input type="date" value={newStartDate} onChange={e => setNewStartDate(e.target.value)} className="h-8 text-xs" data-testid="add-milestone-start-date" />
            </div>
            <div className="flex-1">
              <Label className="text-[10px] text-neutral-400 uppercase">End Date</Label>
              <Input type="date" value={newEndDate} onChange={e => setNewEndDate(e.target.value)} className="h-8 text-xs" data-testid="add-milestone-end-date" />
            </div>
          </div>
          {/* Dependencies */}
          {milestones.length > 0 && (
            <div className="mt-2">
              <Label className="text-[10px] text-neutral-400 uppercase">Depends On</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {milestones.filter(m => !m.done).map(ms => (
                  <button key={ms.id} type="button"
                    onClick={() => setNewDeps(prev => prev.includes(ms.id) ? prev.filter(d => d !== ms.id) : [...prev, ms.id])}
                    className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${newDeps.includes(ms.id) ? "bg-blue-100 border-blue-300 text-blue-700" : "bg-neutral-50 border-neutral-200 text-neutral-500 hover:bg-neutral-100"}`}>
                    {ms.title}
                  </button>
                ))}
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 mt-2 cursor-pointer">
            <input type="checkbox" checked={newCritical} onChange={e => setNewCritical(e.target.checked)} className="rounded" />
            <span className="text-[10px] text-red-500 font-medium">Mark as Critical Path</span>
          </label>
        </CardContent>
      </Card>

      {/* Milestone List */}
      {milestones.length > 0 ? (
        <div className="space-y-2">
          {milestones.map((ms, i) => {
            const msId = ms.id || ms.title;
            const isEditMode = editingId === msId;
            const overdue = isOverdue(ms);
            const msStatus = ms.status || (ms.done ? "completed" : "in_progress");
            const endDate = ms.end_date || ms.due_date;
            const daysLeft = daysUntil(endDate);

            return (
              <div
                key={msId}
                className={`rounded-xl border transition-all group ${
                  msStatus === "completed" ? "bg-emerald-50/60 border-emerald-200" :
                  msStatus === "waiting_client" ? "bg-amber-50/60 border-amber-200" :
                  overdue ? "bg-red-50/60 border-red-200" :
                  "bg-white border-neutral-200 hover:border-neutral-300"
                }`}
                data-testid={`milestone-${i}`}
              >
                {/* Main row */}
                <div className="flex items-start gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    {isEditMode ? (
                      <div className="space-y-2">
                        <Input value={editTitle} onChange={e => setEditTitle(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") handleSaveEdit(ms); if (e.key === "Escape") setEditingId(null); }}
                          className="h-8 text-sm" autoFocus />
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-[10px] text-neutral-400">Start Date</Label>
                            <Input type="date" value={editStartDate} onChange={e => setEditStartDate(e.target.value)} className="h-7 text-xs" />
                          </div>
                          <div className="flex-1">
                            <Label className="text-[10px] text-neutral-400">End Date</Label>
                            <Input type="date" value={editEndDate} onChange={e => setEditEndDate(e.target.value)} className="h-7 text-xs" />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleSaveEdit(ms)}>Save</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingId(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className={`text-sm font-medium ${msStatus === "completed" ? "line-through text-emerald-700/70" : overdue ? "text-red-800" : "text-neutral-900"}`}>
                            {ms.title}
                          </p>
                          <StatusBadge status={msStatus} />
                        </div>

                        {/* Date info row */}
                        <div className="flex items-center gap-3 flex-wrap">
                          {ms.start_date && (
                            <span className="text-[10px] text-neutral-500 flex items-center gap-1">
                              <CalendarDays className="w-2.5 h-2.5" /> Start: {fmtDate(ms.start_date)}
                            </span>
                          )}
                          {endDate && msStatus !== "completed" && (
                            <span className={`text-[10px] flex items-center gap-1 ${
                              overdue ? "text-red-600 font-semibold" :
                              daysLeft !== null && daysLeft <= 3 ? "text-amber-600 font-medium" : "text-neutral-500"
                            }`}>
                              <CalendarDays className="w-2.5 h-2.5" />
                              {overdue ? `Overdue by ${Math.abs(daysLeft)}d` : daysLeft === 0 ? "Due today" : daysLeft === 1 ? "Due tomorrow" : `End: ${fmtDate(endDate)} (${daysLeft}d)`}
                            </span>
                          )}
                          {endDate && msStatus === "completed" && (
                            <span className="text-[10px] text-neutral-400 flex items-center gap-1">
                              <CalendarDays className="w-2.5 h-2.5" /> End: {fmtDate(endDate)}
                            </span>
                          )}
                          {ms.completed_at && (
                            <span className="text-[10px] text-emerald-600 flex items-center gap-1">
                              <CheckCircle2 className="w-2.5 h-2.5" /> Completed {fmtDate(ms.completed_at)}
                            </span>
                          )}
                          {!ms.start_date && !endDate && (
                            <span className="text-[10px] text-neutral-400 flex items-center gap-1">
                              <Clock className="w-2.5 h-2.5" /> Added {fmtDate(ms.created_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {!isEditMode && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 pt-0.5">
                      <button onClick={() => { setEditingId(msId); setEditTitle(ms.title); setEditStartDate(ms.start_date || ""); setEditEndDate(ms.end_date || ms.due_date || ""); }}
                        className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600" title="Edit" data-testid={`milestone-edit-${i}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(ms)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-neutral-400 hover:text-red-500" title="Remove" data-testid={`milestone-delete-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Status buttons row */}
                {!isEditMode && (
                  <div className="flex items-center gap-1.5 px-4 pb-3 pt-0" data-testid={`milestone-status-row-${i}`}>
                    <StatusButton status="in_progress" currentStatus={msStatus} onClick={() => handleStatusChange(ms, "in_progress")} />
                    <StatusButton status="waiting_client" currentStatus={msStatus} onClick={() => handleStatusChange(ms, "waiting_client")} />
                    <StatusButton status="completed" currentStatus={msStatus} onClick={() => handleStatusChange(ms, "completed")} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-neutral-400">
          <Target className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No milestones yet</p>
          <p className="text-xs mt-1">Add milestones to track project progress</p>
        </div>
      )}
    </div>
  );
}
